import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardHeader, CardBody, Table,
  Button, Input, Badge, Spinner, InputGroup, Alert, Label,
} from "reactstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import controlPlaneApi from "../../../api/controlPlane";
import programsApi from "../../../api/programs";

// Admin renewal / plan change (spec 003). Pick a customer, optionally a new plan,
// renew — no payment (billing is external). On a downgrade the result flags which
// identity fields the customer must trim in the portal ("choose what to keep").

const readErr = (e, fb) => e?.response?.data?.message || e?.message || fb;
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const RenewalPage = () => {
  const tenant = useSelector((s) => s?.clients?.selectedClient?.value) || "cyberior";

  const [customerId, setCustomerId] = useState("");
  const [programs, setPrograms] = useState([]);
  const [targetProgramId, setTargetProgramId] = useState(""); // "" = keep same plan
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await programsApi.getPrograms({ tenantId: tenant });
        const list = res?.data?.data || res?.data?.programs || res?.data || [];
        setPrograms(Array.isArray(list) ? list : []);
      } catch (e) {
        toast.error(readErr(e, "Failed to load plans"));
      }
    })();
  }, [tenant]);

  const renew = async () => {
    if (!customerId) {
      toast.warn("Enter a customer ID");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const res = await controlPlaneApi.adminRenew(
        tenant,
        Number(customerId),
        targetProgramId ? Number(targetProgramId) : undefined,
      );
      setResult(res?.data || {});
      toast.success(res?.data?.plan_changed ? "Plan changed & renewed" : "Renewed");
    } catch (e) {
      toast.error(readErr(e, "Renewal failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid className="p-4">
      <Row className="mb-3">
        <Col className="d-flex align-items-center justify-content-between flex-wrap">
          <div>
            <h4 className="mb-1">Renew / Change Plan</h4>
            <span className="text-muted">
              Admin renewal — no payment (billing is handled externally).
            </span>
          </div>
          <Badge color="primary" pill>tenant: {tenant}</Badge>
        </Col>
      </Row>

      <Row>
        <Col lg={7} xl={6}>
          <Card>
            <CardHeader><strong>Renew a customer</strong></CardHeader>
            <CardBody>
              <div className="mb-3">
                <Label className="fw-bold">Customer ID</Label>
                <Input
                  type="number"
                  placeholder="e.g. 1042"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <Label className="fw-bold">Plan</Label>
                <Input
                  type="select"
                  value={targetProgramId}
                  onChange={(e) => setTargetProgramId(e.target.value)}
                >
                  <option value="">Keep same plan (renew)</option>
                  {programs.map((p) => (
                    <option key={p.ID} value={p.ID}>
                      {p.name}{p.validity ? ` — ${p.validity}` : ""}
                    </option>
                  ))}
                </Input>
                <small className="text-muted">
                  Choosing a smaller plan may put the customer over a field limit — they’ll
                  be asked to choose what to keep on their next visit.
                </small>
              </div>

              <Button color="primary" onClick={renew} disabled={submitting}>
                {submitting ? <Spinner size="sm" /> : "Renew / Change plan"}
              </Button>
            </CardBody>
          </Card>
        </Col>

        <Col lg={5} xl={6}>
          {result && (
            <Card>
              <CardHeader><strong>Result</strong></CardHeader>
              <CardBody>
                <Table borderless size="sm" className="mb-3">
                  <tbody>
                    <tr><td className="text-muted">Customer</td><td>#{result.customer_id}</td></tr>
                    <tr><td className="text-muted">Program</td><td>{result.program_id}</td></tr>
                    <tr>
                      <td className="text-muted">Plan changed</td>
                      <td>
                        <Badge color={result.plan_changed ? "info" : "secondary"}>
                          {result.plan_changed ? "yes" : "renewed same"}
                        </Badge>
                      </td>
                    </tr>
                    <tr><td className="text-muted">New expiry</td><td>{fmt(result.expiration_date)}</td></tr>
                  </tbody>
                </Table>

                {result.reconciliationNeeded ? (
                  <Alert color="warning" className="mb-0">
                    <strong>Downgrade — customer must trim these fields.</strong>
                    <div className="mt-2">
                      {(result.overLimitFields || []).map((f) => (
                        <div key={f.field}>
                          <code>{f.field}</code>: has {f.currentCount}, new max {f.maxSlot}
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 small">
                      They’ll be prompted in the portal to choose which values to keep.
                    </div>
                  </Alert>
                ) : (
                  <Alert color="success" className="mb-0">No field trimming needed.</Alert>
                )}
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default RenewalPage;
